import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, DollarSign, Users, BookOpen, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MOCK_COURSES, MOCK_CATEGORIES } from '../utils/mockData';

// Demo-only admin users and purchases for UI
const MOCK_ADMIN_USERS = [
  {
    id: 'u_1',
    name: 'Admin thesksaif',
    email: 'thesksaif@gmail.com',
    role: 'admin',
    created_at: new Date().toISOString(),
  },
  {
    id: 'u_2',
    name: 'Jane Doe',
    email: 'jane@example.com',
    role: 'student',
    created_at: new Date().toISOString(),
  },
  {
    id: 'u_3',
    name: 'David Smith',
    email: 'david@example.com',
    role: 'student',
    created_at: new Date().toISOString(),
  },
];

const MOCK_ADMIN_PURCHASES = [
  {
    id: 'ord_1001',
    user_id: 'u_2',
    course_id: 1,
    amount: 49.99,
    payment_status: 'paid',
    created_at: new Date().toISOString(),
  },
  {
    id: 'ord_1002',
    user_id: 'u_3',
    course_id: 2,
    amount: 59.99,
    payment_status: 'paid',
    created_at: new Date().toISOString(),
  },
];

function AdminDashboard() {
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [courses, setCourses] = useState(MOCK_COURSES);
  const [categories, setCategories] = useState(MOCK_CATEGORIES);
  const [users] = useState(MOCK_ADMIN_USERS);
  const [purchases] = useState(MOCK_ADMIN_PURCHASES);
  const [showModal, setShowModal] = useState(false);

  // Sync activeTab with URL params
  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  // Derived overview stats from current demo data
  const stats = {
    total_users: users.length,
    total_courses: courses.length,
    total_purchases: purchases.length,
    total_revenue: purchases.reduce((sum, p) => sum + p.amount, 0),
  };

  // Demo-only CRUD handlers (UI only, no backend)
  const handleCreateCourse = (courseData) => {
    const nextId = courses.length ? Math.max(...courses.map((c) => c.id)) + 1 : 1;
    const newCourse = {
      id: nextId,
      students_count: 0,
      rating: 0,
      ...courseData,
    };
    setCourses((prev) => [...prev, newCourse]);
    toast.success('Course created (demo only)');
  };

  const handleUpdateCourse = (courseId, courseData) => {
    setCourses((prev) =>
      prev.map((course) => (course.id === courseId ? { ...course, ...courseData } : course)),
    );
    toast.success('Course updated (demo only)');
  };

  const handleDeleteCourse = (courseId) => {
    if (!window.confirm('Delete this course?')) return;
    setCourses((prev) => prev.filter((course) => course.id !== courseId));
    toast.success('Course deleted (demo only)');
  };

  const handleCreateCategory = (categoryData) => {
    const nextId = categories.length ? Math.max(...categories.map((c) => c.id)) + 1 : 1;
    const newCategory = {
      id: nextId,
      ...categoryData,
    };
    setCategories((prev) => [...prev, newCategory]);
    toast.success('Category created (demo only)');
  };

  return (
    <div className="p-8" data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-4xl font-extrabold text-slate-900 mb-2">Admin Dashboard</h1>
        <p className="text-slate-600">Manage your education platform</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
        {['overview', 'courses', 'categories', 'users', 'purchases'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 rounded-xl font-semibold whitespace-nowrap transition-colors ${activeTab === tab
              ? 'bg-blue-600 text-white'
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
              }`}
            data-testid={`tab-${tab}`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'overview' && <OverviewTab stats={stats} />}
      {activeTab === 'courses' && (
        <CoursesTab
          courses={courses}
          categories={categories}
          onDeleteCourse={handleDeleteCourse}
        />
      )}
      {activeTab === 'categories' && (
        <CategoriesTab
          categories={categories}
          onCreateCategory={handleCreateCategory}
          showModal={showModal}
          setShowModal={setShowModal}
        />
      )}
      {activeTab === 'users' && <UsersTab users={users} />}
      {activeTab === 'purchases' && <PurchasesTab purchases={purchases} />}
    </div>
  );
}

function OverviewTab({ stats }) {
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-extrabold text-slate-900">{stats.total_users}</div>
              <div className="text-slate-600 mt-1">Total Users</div>
            </div>
            <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center">
              <Users className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-extrabold text-slate-900">{stats.total_courses}</div>
              <div className="text-slate-600 mt-1">Total Courses</div>
            </div>
            <div className="w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-extrabold text-slate-900">{stats.total_purchases}</div>
              <div className="text-slate-600 mt-1">Total Sales</div>
            </div>
            <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-extrabold text-slate-900">${stats.total_revenue.toFixed(2)}</div>
              <div className="text-slate-600 mt-1">Total Revenue</div>
            </div>
            <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center">
              <DollarSign className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CoursesTab({ courses, categories, onDeleteCourse }) {
  const navigate = useNavigate();

  const handleDelete = async (courseId) => {
    onDeleteCourse(courseId);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-slate-900">Courses</h2>
        <button
          onClick={() => navigate('/admin/course/new')}
          className="btn-primary flex items-center gap-2"
          data-testid="add-course-button"
        >
          <Plus className="w-5 h-5" />
          Add Course
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Course</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Category</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Price</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Students</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Rating</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {courses.map((course) => {
                // Extract category lookup to avoid nested member expression recursion
                const category = categories.find((c) => c.id === course.category_id);
                const categoryName = category ? category.name : 'N/A';

                return (
                  <tr key={course.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img src={course.thumbnail} alt={course.title} className="w-16 h-10 rounded object-cover" />
                        <div className="font-semibold text-slate-900">{course.title}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {categoryName}
                    </td>
                    <td className="px-6 py-4 text-slate-900 font-semibold">${course.price}</td>
                    <td className="px-6 py-4 text-slate-600">{course.students_count}</td>
                    <td className="px-6 py-4 text-slate-600">{course.rating || 0}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/admin/course/edit/${course.id}`)}
                          className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"
                          data-testid={`edit-course-${course.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(course.id)}
                          className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                          data-testid={`delete-course-${course.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CategoriesTab({ categories, onCreateCategory, showModal, setShowModal }) {
  const [formData, setFormData] = useState({ name: '', description: '', icon: 'BookOpen' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    onCreateCategory(formData);
    toast.success('Category created (demo only)');
    setShowModal(false);
    setFormData({ name: '', description: '', icon: 'BookOpen' });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-slate-900">Categories</h2>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="add-category-button"
        >
          <Plus className="w-5 h-5" />
          Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <div key={category.id} className="bg-white rounded-2xl p-6 border border-slate-100">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">{category.name}</h3>
            <p className="text-slate-600 text-sm">{category.description}</p>
          </div>
        ))}
      </div>

      {/* Category Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="category-form">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-field"
                required
                data-testid="category-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-field min-h-24"
                required
                data-testid="category-description-input"
              />
            </div>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setFormData({ name: '', description: '', icon: 'BookOpen' });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary" data-testid="save-category-button">
                Create Category
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UsersTab({ users }) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 mb-6">Users</h2>
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Role</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 font-semibold text-slate-900">{user.name}</td>
                  <td className="px-6 py-4 text-slate-600">{user.email}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`badge ${user.role === 'admin' ? 'badge-amber' : 'badge-blue'
                        }`}
                    >
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PurchasesTab({ purchases }) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 mb-6">Purchases</h2>
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Order ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">User ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Course ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Amount</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {purchases.map((purchase) => (
                <tr key={purchase.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 font-mono text-sm text-slate-600">{purchase.id.slice(0, 16)}...</td>
                  <td className="px-6 py-4 font-mono text-sm text-slate-600">{purchase.user_id.slice(0, 16)}...</td>
                  <td className="px-6 py-4 font-mono text-sm text-slate-600">{String(purchase.course_id)}</td>
                  <td className="px-6 py-4 text-slate-900 font-semibold">${purchase.amount}</td>
                  <td className="px-6 py-4">
                    <span className="badge badge-blue">{purchase.payment_status}</span>
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {new Date(purchase.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
